import { Declaration, parse, Rule } from "css";
import { colorDefinitionPrintValue, svgColorValueToDefinition } from "./color";
import { domParser, xmlSerializer } from "./crossplatform";
import { ColorDefinition } from "../types";

const fillableTagNames = [
    "altGlyph",
    "circle",
    "ellipse",
    "path",
    "polygon",
    "polyline",
    "rect",
    "text",
    "textPath",
    "tref",
    "tspan",
];

/** These fields in an inline style should be moved to an attribute */
const replaceableStyleNames = ["fill", "stroke"];

/*
 * Apply a function to a DOM node and all its children.
 */
const traverse = (node: Element, f: (node: Element) => void) => {
    f(node);
    if (node.children.length > 0) {
        Array.from(node.children).forEach((child) => traverse(child, f));
    }
};

/*
 * Succesively check a node's parents for a value, stopping when one is found or when we pass the root.
 */
const checkAncestors = <T>(node: Element, f: (node: Element) => (undefined | T)): (undefined | T) => {
    const candidate = f(node);
    if (candidate) {
        return candidate;
    }
    const parent = node.parentElement;
    if (!parent) {
        return undefined;
    }
    return checkAncestors(parent, f);
};

/**
 * Whether an SVG element matches a CSS selector.
 */
const matchingSelector = (node: Element, selector: string): boolean => {
    try {
        return node.matches(selector);
    } catch (e) {
        // Currently the only known type of failure is if a
        // class name contains a backtick.
        return node.classList.contains(selector.substring(1));
    }
};

/*
 * Prepare SVG to be embedded.
 */
const sanitizeSvgTree = (root: Element, preserveAspectRatio = false) => {
    // Set this so region height and width are respected.
    !preserveAspectRatio && root.setAttribute("preserveAspectRatio", "none");

    // Discard script tags.
    // Convert style tags to per-element styles.
    const rules: Rule[] = [];
    traverse(root, (node) => {
        if (node.tagName === "script") {
            node.remove();
            return;
        }
        if (node.tagName === "style") {
            try {
                const tree = parse(node.innerHTML);
                tree.stylesheet?.rules.forEach((rule: Rule) => {
                    rules.push(rule);
                });
                node.remove();
            } catch (e) {
                console.error(e);
            }
            return;
        }
        const nodeStyle = node.attributes.getNamedItem("style")?.value?.trim();
        if (nodeStyle) {
            const styles: string[] = [];
            const styleStrings = nodeStyle.split(";");
            styleStrings.forEach((style) => {
                const trimmed = style.trim();
                if (trimmed) {
                    const [field, value] = trimmed.split(":");
                    const fieldDowncased = field.toLowerCase();
                    const idx = replaceableStyleNames.indexOf(fieldDowncased);
                    if (idx > -1) {
                        node.setAttribute(fieldDowncased, value.trim());
                    } else {
                        styles.push(trimmed);
                    }
                }
            });
            if (styles.length > 0) {
                node.setAttribute("style", styles.join(";"));
            } else {
                node.removeAttribute("style");
            }
        }
    });
    traverse(root, (node) => {
        rules.forEach((rule) => {
            rule.selectors?.forEach((selector) => {
                if (matchingSelector(node, selector)) {
                    rule.declarations?.forEach((decl: Declaration) => {
                        if (decl.property && decl.value) {
                            node.setAttribute(decl.property, decl.value);
                        }
                    });
                }
            });
        });
    });
};

/*
 * SVG files shouldn't contain a width and height. Only a viewbox.
 * The platform handles width and height independant.
 */
const sanitizeSVGDimensions = (root: Element) => {
    const nonPixelUnitsRegex = /pt|pc|mm|cm|in/gm;
    const viewBox = root.getAttribute("viewBox");
    const width = root.getAttribute("width");
    const height = root.getAttribute("height");
    if (!viewBox) throw new Error("viewBox not specified on SVG!");
    if (width) root.setAttribute("width", width.replace(nonPixelUnitsRegex, ""));
    if (height) root.setAttribute("height", height.replace(nonPixelUnitsRegex, ""));
};

const svgStringToElement = (svg: string): Element | null => {
    const parser = domParser();
    const parsedSvg = parser.parseFromString(svg, "image/svg+xml");
    return parsedSvg.firstElementChild;
};

const setSvgDimensions = (svgRoot: Element, width: number, height: number) => {
    svgRoot.setAttribute("height", `${height}px`);
    svgRoot.setAttribute("width", `${width}px`);
};

const setSvgColors = (svgRoot: Element, colors: { [key: string]: ColorDefinition }, includeSpotColors?: boolean) => {
    traverse(svgRoot, (node) => {
        // Handle fills set directly on elements.
        const fill = node.attributes.getNamedItem("fill");
        if (fill && fill.value !== "none") {
            node.classList.forEach((className) => {
                if (className.startsWith("spiff-fill")) {
                    const color = colors[className.replace("spiff-fill-", "")];
                    if (color) {
                        node.setAttribute(
                            "fill",
                            includeSpotColors ? colorDefinitionPrintValue(color) : color.browserValue,
                        );
                    } else {
                        // legacy
                        const color = colors[className];
                        if (color) {
                            node.setAttribute(
                                "fill",
                                includeSpotColors ? colorDefinitionPrintValue(color) : color.browserValue,
                            );
                        }
                    }
                }
            });
        }
        const stroke = node.attributes.getNamedItem("stroke");
        if (stroke && stroke.value !== "none") {
            node.classList.forEach((className) => {
                if (className.startsWith("spiff-stroke")) {
                    const color = colors[className.replace("spiff-stroke-", "")];
                    if (color) {
                        node.setAttribute(
                            "stroke",
                            includeSpotColors ? colorDefinitionPrintValue(color) : color.browserValue,
                        );
                    } else {
                        // legacy
                        const color = colors[className];
                        if (color) {
                            node.setAttribute(
                                "fill",
                                includeSpotColors ? colorDefinitionPrintValue(color) : color.browserValue,
                            );
                        }
                    }
                }
            });
        }
    });
};

const svgElementToString = (svgRoot: Element): string => {
    const serialiser = xmlSerializer();
    return serialiser.serializeToString(svgRoot);
};

const modifySVGWithElementProperties = (
    svg: string,
    width: number,
    height: number,
    colors: { [key: string]: ColorDefinition },
    includeSpotColors?: boolean,
): string => {
    const svgRoot = svgStringToElement(svg);
    if (!svgRoot) {
        throw new Error("Failed to read SVG.");
    }
    setSvgDimensions(svgRoot, width, height);
    setSvgColors(svgRoot, colors, includeSpotColors);
    return svgElementToString(svgRoot);
};

const modifySVGColors = (
    svg: string,
    colors: { [key: string]: ColorDefinition },
    includeSpotColors?: boolean,
): string => {
    const svgRoot = svgStringToElement(svg);
    if (!svgRoot) {
        throw new Error("Failed to read SVG.");
    }
    setSvgColors(svgRoot, colors, includeSpotColors);
    return svgElementToString(svgRoot);
};

const parseSvg = (svgBody: string): any => {
    // Get the raw SVG without a doctype.
    const svgRegex = /<svg.*<\/svg>/s;
    const svgMatches = svgBody.match(svgRegex) || [];
    const svg = svgMatches?.length > 0 ? svgMatches[0] : "";

    // Parse the SVG into a tree.
    const parser = domParser();
    return parser.parseFromString(svg, "image/svg+xml");
};

const generateSVGWithUnknownColors = async (
    svgBody: string,
): Promise<{ svg: string; colors: { [key: string]: ColorDefinition } }> => {
    const parsedSvg = parseSvg(svgBody);
    const root = parsedSvg.firstElementChild;
    if (!root) {
        throw new Error("Failed to read SVG.");
    }

    sanitizeSvgTree(root);

    // Annotate elements which have fills with a new class name.
    const colors: { [key: string]: ColorDefinition } = {};
    traverse(root, (node) => {
        // A fillable element's fill is:
        // * The fill in its style; otherwise
        // * The fill in its fill attribute; otherwise
        // * The fill of its closest ancestor (style takes priority over attribute); otherwise
        // * Black (none of its ancestors have an explicit fill).
        // Historically we have not cared about the first case, but for the third case the
        // code for attaching explicit fills to elements without explicit fills needs to
        // successively check its ancestors.

        if (fillableTagNames.includes(node.tagName) && !node.attributes.getNamedItem("fill")) {
            let fill = "black";
            const parent = node.parentElement;
            if (parent) {
                const ancestorFill = checkAncestors(parent, (node: Element) => {
                    const inlineStyles = (node as SVGElement).style;
                    const inlineFill = inlineStyles.fill;
                    if (inlineFill) {
                        return inlineFill;
                    }
                    const attribute = node.getAttribute("fill");
                    if (attribute) {
                        return attribute;
                    }
                    return undefined;
                });
                if (ancestorFill) {
                    fill = ancestorFill;
                }
            }
            node.setAttribute("fill", fill);
        }

        const fill = node.attributes.getNamedItem("fill");
        if (fill && fill.value !== "none" && !fill.value.startsWith("url(")) {
            const fillValue = svgColorValueToDefinition(fill.value);
            const alphanumericFill = fillValue.browserValue.replace(/\W/g, "");
            const className = `spiff-fill-${alphanumericFill}`;
            node.setAttribute("fill", fillValue.browserValue);
            node.classList.add(className);
            colors[alphanumericFill] = fillValue;
        }
        const stroke = node.attributes.getNamedItem("stroke");
        if (stroke && stroke.value !== "none" && !stroke.value.startsWith("url(")) {
            const strokeValue = svgColorValueToDefinition(stroke.value);
            const alphanumericFill = strokeValue.browserValue.replace(/\W/g, "");
            const className = `spiff-stroke-${alphanumericFill}`;
            node.classList.add(className);
            node.setAttribute("stroke", strokeValue.browserValue);
            colors[alphanumericFill] = strokeValue;
        }
    });

    const serialiser = xmlSerializer();
    const rebuiltSvg = serialiser.serializeToString(root);

    return {
        colors,
        svg: rebuiltSvg,
    };
};

export {
    sanitizeSvgTree,
    sanitizeSVGDimensions,
    traverse,
    modifySVGWithElementProperties,
    modifySVGColors,
    generateSVGWithUnknownColors,
};
